import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ConfirmModal from '@domain/confirm-modal';
import PDFModal from '@domain/pdf-modal';
import { getConfigurationAssetUrl } from '@services/api/constants';
import { useVismaConfiguration } from '@services/visitor-management';
import { useConfigurationStore } from '@store/use-configuration-store';

import { useRegisterPage } from './register-page-context';

const Container = styled.div`
  width: 100%;
  margin-bottom: 3em;
`;

const HeaderText = styled.h1`
  font-size: 30px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  padding-bottom: 16px;

  @media only screen and (max-width: 1200px) {
    margin: 16px 20px;
  }
`;

const DescText = styled.h6`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};
  margin: 24px 0;
  white-space: break-spaces;

  & > a {
    color: ${({ theme }) => theme.primary};

    &:hover {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 16px;

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
    & > input {
      min-width: 20px;
    }
  }
`;

const Radio = styled.input`
  width: 2em;
  height: 2em;
  accent-color: ${({ theme }) => theme.primary};
  margin-right: 1em;
`;

const GrayText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
`;

const ErrorText = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.error};
  margin-top: 12px;
`;

interface DataProtectionProps {
  onDecline: () => void;
}

function DataProtection({ onDecline }: DataProtectionProps) {
  const intl = useIntl();
  const { configuration } = useVismaConfiguration();
  const options = configuration?.dataProtection?.expiryOptions ?? [];
  const { control, setFormField } = useRegisterPage()!;
  const { friendly_url, module } = useConfigurationStore();
  const [confirmModal, setConfirmModal] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);

  const documentId = configuration?.dataProtection?.document?.id;
  const documentLink = configuration?.dataProtection?.document_link;
  const documentUrl = documentId
    ? getConfigurationAssetUrl(
        friendly_url,
        module,
        'image',
        String(documentId)
      )
    : null;

  return (
    <Container>
      <ConfirmModal
        open={confirmModal}
        closeModal={() => {
          setConfirmModal(false);
          setFormField('expiry', '');
        }}
        leftButtonTitle={intl.formatMessage({ id: 'CLOSE' })}
        rightButtonTitle={intl.formatMessage({ id: 'FINISH_AND_EXIT' })}
        onQuit={onDecline}
        leftButtonWithArrow={false}
        rightButtonWithArrow={false}
      />
      {pdfModal && <PDFModal closeModal={() => setPdfModal(false)} />}
      <HeaderText>{configuration?.dataProtection?.title}</HeaderText>
      <DescText
        onClick={(e) => {
          if ((e.target as HTMLElement).tagName === 'A') {
            setPdfModal(true);
          }
        }}
        dangerouslySetInnerHTML={{
          __html: configuration?.dataProtection?.description ?? ''
        }}
      />
      {documentUrl && documentLink && (
        <DescText>
          <a href={documentUrl} target="_blank" rel="noreferrer">
            {documentLink}
          </a>
        </DescText>
      )}
      <Controller
        control={control}
        name="expiry"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            {options.map((option) => {
              const handleSelect = () => {
                if (option.decline) {
                  window.scroll({ top: 0, behavior: 'smooth' });
                  setConfirmModal(true);
                }
                onChange(option.value);
              };
              return (
                <RadioGroup key={option.value}>
                  <Radio
                    type="radio"
                    checked={value === option.value}
                    onChange={(e) => e.target.checked && handleSelect()}
                  />
                  <GrayText onClick={handleSelect}>{option.text}</GrayText>
                </RadioGroup>
              );
            })}
            {error && (
              <ErrorText>
                {intl.formatMessage({
                  id: error.message,
                  defaultMessage: error.message
                })}
              </ErrorText>
            )}
          </>
        )}
      />
    </Container>
  );
}

export default DataProtection;
